import React, {
  ReactElement, useEffect, useRef, useState,
} from 'react'
import styled from 'styled-components'
import colors from '../../lib/colors'
import theme, { ZIndex } from '../../lib/theme'
import { IColorTheme } from '../../types/itemTypes'

const Styles = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  column-gap: 4px;
  color: ${(props): string => props.theme.colourHeaderText};
  font-size: 13px;
`
const Container = styled.div`
  position: relative;
  width: 32px;
`
const SelectButton = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-around;
  border: 1px solid ${(props): string => props.theme.fade};
  color: ${(props): string => props.theme.colourHeaderText};
  background-color: transparent;
  height: 20px;
  width: 100%;
  padding: 0 3px;
  font-family: inherit;
  font-size: 13px;
  .select-button-arrow {
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 6px solid ${(props): string => props.theme.fade};
    margin-top: 2px;
    margin-left: 1px;
  }
  &.select-button-arrow-transparent .select-button-arrow {
    border-color: transparent;
  }
`
const IndividualsDropdown = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 154px;
  border: 1px solid ${colors.darkGray};
  color: ${(props): string => props.theme.colourHeaderText};
  box-sizing: border-box;
  background-color: ${colors.white};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow-y: scroll;
  ${theme.itemScrollbar};
  position: absolute;
  z-index: ${ZIndex.AboveContent};
`
const DropdownItem = styled.div`
  cursor: pointer;
  padding: 5px;
  font-size: 13px;
  &.dropdown-selected {
    background-color: ${colors.gray};
  }
  :hover {
    background-color: ${(props): string => props.theme.colourHighlight};
    color: ${(props): string => props.theme.colourTextOnHighlight};
  }
`
interface IIndividualsProps {
  maxIndividuals: number
  individuals: number
  setIndividuals: (individuals: number) => void
  colorTheme: IColorTheme
}
export default function Individuals(props: IIndividualsProps): ReactElement {
  const {
    maxIndividuals, individuals, setIndividuals, colorTheme,
  } = props

  const [showDropdown, setShowDropdown] = useState(false)

  const contentRef = useRef<HTMLDivElement>(null)
  const handleClickOutside = (mouseDownEvent: MouseEvent): void => {
    const ref = contentRef.current
    const { target } = mouseDownEvent
    if (target instanceof Node && ref && !ref.contains(target)) {
      setShowDropdown(false)
    }
  }
  const handleKeydown = (keyDownEvent: KeyboardEvent): void => {
    if (keyDownEvent.code === 'Escape') setShowDropdown(false)
  }
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [contentRef])

  const handleSelectValue = (value: number): void => {
    setIndividuals(value)
    setShowDropdown(false)
  }

  const getDropdownItems = (): number[] => {
    const items = []
    for (let i = 1; i <= maxIndividuals; i += 1) items.push(i)
    return items
  }

  return (
    <Styles theme={colorTheme}>
      For
      <Container ref={contentRef}>
        <SelectButton
          onClick={(): void => setShowDropdown((prevState) => !prevState)}
          theme={colorTheme}
          className={showDropdown ? 'select-button-arrow-transparent' : ''}
        >
          {individuals}
          <div className="select-button-arrow" />
        </SelectButton>
        {showDropdown && (
          <IndividualsDropdown theme={colorTheme}>
            {getDropdownItems().map((item) => (
              <DropdownItem
                key={item}
                onClick={(): void => handleSelectValue(item)}
                className={individuals === item ? 'dropdown-selected' : ''}
                theme={colorTheme}
              >
                {item}
              </DropdownItem>
            ))}
          </IndividualsDropdown>
        )}
      </Container>
      {`${individuals > 1 ? 'people' : 'person'}`}
    </Styles>
  )
}
